import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ShieldCheck, GraduationCap, CheckCircle2 } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const LS_TOKEN = "ccchaps.pvc.token";

// ---------------------------------------------------------------------------
// PVC-001 Sandbox Enrollment Flow
// ---------------------------------------------------------------------------
// Steps: email-check → (register | signin) → order-review → paypal → result
// PayPal button is stubbed until credentials arrive; server-side capture is the
// authoritative gate. Simply reaching the success view does NOT grant access.
// ---------------------------------------------------------------------------

export default function AcademyPvcEnroll() {
  usePageMeta({
    title: "Enroll · PVC-001 The Patient's Voice in Care",
    description: "Sandbox enrollment flow for PVC-001. Not currently open for public purchase.",
    noindex: true,
  });

  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState("email");             // email | register | signin | review | pay | success | error
  const [email, setEmail] = useState("");
  const [existing, setExisting] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "" });
  const [token, setToken] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  // If a token already exists, jump straight to review
  useEffect(() => {
    if (token && step === "email") setStep("review");
  }, [token]); // eslint-disable-line

  const api = axios.create({ baseURL: `${API}/api`, headers: token ? { Authorization: `Bearer ${token}` } : {} });

  const submitEmail = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post("/portal/pvc/check-email", { email });
      setExisting(!!r.data.exists);
      setStep(r.data.exists ? "signin" : "register");
    } catch (e) { setErr(e?.response?.data?.detail || "Unable to check email."); }
    finally { setBusy(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post("/portal/pvc/register", {
        email, password: form.password,
        first_name: form.first_name, last_name: form.last_name,
        course_comms_consent: true, privacy_policy_agreed: true,
        terms_agreed: true, code_of_conduct_agreed: true,
      });
      setToken(r.data.access_token);
      localStorage.setItem(LS_TOKEN, r.data.access_token);
      setStep("review");
    } catch (e) { setErr(e?.response?.data?.detail || "Registration failed."); }
    finally { setBusy(false); }
  };

  const submitSignin = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/api/portal/auth/login`, { email, password: form.password });
      setToken(r.data.access_token);
      localStorage.setItem(LS_TOKEN, r.data.access_token);
      setStep("review");
    } catch (e) { setErr(e?.response?.data?.detail || "Sign-in failed."); }
    finally { setBusy(false); }
  };

  const startOrder = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/portal/pvc/orders", { course_code: "PVC-001" });
      setOrder(r.data);
      setStep("pay");
    } catch (e) {
      if (e?.response?.status === 409) {
        // Already entitled — jump straight to success.
        setResult({ status: "completed", entitlement_active: true, ux_state: "activated", already: true });
        setStep("success");
      } else {
        setErr(e?.response?.data?.detail || "Could not create order.");
      }
    }
    finally { setBusy(false); }
  };

  const captureOrder = async () => {
    if (!order) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/portal/pvc/orders/${order.paypal_order_id}/capture`, {});
      setResult(r.data);
      setStep(r.data.status === "completed" && r.data.entitlement_active ? "success" : "error");
    } catch (e) { setErr(e?.response?.data?.detail || "Capture failed."); setStep("error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF6]" data-testid="pvc-enroll-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">CCCHAPS Academy · PVC-001</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight" data-testid="pvc-enroll-title">
          Enroll — The Patient&rsquo;s Voice in Care
        </h1>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800">Sandbox Testing</span>
          <span className="text-xs text-slate-500">This flow is not open to the public and does not move real money.</span>
        </div>

        {/* Steps indicator */}
        <ol className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
          {["email", "register/signin", "review", "pay", "done"].map((label, i) => (
            <li key={label} className={`px-2 py-1 rounded-full border ${indexActive(step, i) ? "border-[#4A6FA5] bg-[#F0F4F8] text-[#4A6FA5]" : "border-slate-200 bg-white"}`}>{i + 1}. {label}</li>
          ))}
        </ol>

        {err && <div className="mt-5 rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm" data-testid="pvc-enroll-error">{err}</div>}

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          {step === "email" && (
            <form onSubmit={submitEmail} className="space-y-4" data-testid="pvc-enroll-step-email">
              <p className="text-sm text-slate-600">Enter your email to begin. If you already have an Academy account, we&rsquo;ll ask you to sign in instead of creating a duplicate.</p>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Email</span>
                <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="pvc-enroll-email" className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm" />
              </label>
              <button type="submit" disabled={busy} data-testid="pvc-enroll-email-continue" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60">Continue <ArrowRight className="w-4 h-4" /></button>
            </form>
          )}

          {step === "signin" && (
            <form onSubmit={submitSignin} className="space-y-4" data-testid="pvc-enroll-step-signin">
              <p className="text-sm text-slate-700"><strong>This email is already registered.</strong> Sign in to continue with your purchase — we won&rsquo;t create a duplicate account.</p>
              <p className="text-sm text-slate-500">{email}</p>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Password</span>
                <input type="password" required value={form.password} onChange={e=>setForm({...form,password:e.target.value})} data-testid="pvc-enroll-password" className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm" />
              </label>
              <button type="submit" disabled={busy} data-testid="pvc-enroll-signin" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60">Sign In <ArrowRight className="w-4 h-4" /></button>
              <button type="button" onClick={()=>setStep("email")} className="ml-3 text-xs text-slate-500 hover:underline">Use a different email</button>
            </form>
          )}

          {step === "register" && (
            <form onSubmit={submitRegister} className="space-y-4" data-testid="pvc-enroll-step-register">
              <p className="text-sm text-slate-600">Create your Academy account. <strong>Your name will be used on your Certificate of Completion.</strong></p>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block"><span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">First name</span>
                  <input required value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})} data-testid="pvc-enroll-first-name" className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm" /></label>
                <label className="block"><span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Last name</span>
                  <input required value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})} data-testid="pvc-enroll-last-name" className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm" /></label>
              </div>
              <label className="block"><span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Email</span>
                <input readOnly value={email} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500" /></label>
              <label className="block"><span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Password (8+ chars)</span>
                <input type="password" required minLength={8} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} data-testid="pvc-enroll-password" className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm" /></label>
              <p className="text-xs text-slate-500">By continuing you agree to CCCHAPS Academy&rsquo;s privacy policy, terms, code of conduct, and to receive course-related emails.</p>
              <button type="submit" disabled={busy} data-testid="pvc-enroll-register" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60">Create Account <ArrowRight className="w-4 h-4" /></button>
            </form>
          )}

          {step === "review" && (
            <div data-testid="pvc-enroll-step-review">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-2">Order review</p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <p className="font-heading text-xl text-slate-900">The Patient&rsquo;s Voice in Care</p>
                <p className="text-sm text-slate-600">Course ID: PVC-001 · Individual Enrollment</p>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="text-sm text-slate-600">Total</span>
                  <span className="font-heading text-3xl text-slate-900" data-testid="pvc-enroll-price">$79.00 USD</span>
                </div>
              </div>
              <button onClick={startOrder} disabled={busy} data-testid="pvc-enroll-to-paypal" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60">Continue to PayPal <ArrowRight className="w-4 h-4" /></button>
              <p className="mt-3 text-[11px] text-slate-500">Sandbox mode · no real money will be charged. Access is granted only after PayPal confirms a completed capture on the server.</p>
            </div>
          )}

          {step === "pay" && order && (
            <div data-testid="pvc-enroll-step-pay">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-2">PayPal Sandbox</p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 leading-relaxed">
                <p><strong>PayPal order created:</strong> <code className="text-xs">{order.paypal_order_id}</code></p>
                <p className="mt-1"><strong>Environment:</strong> {order.environment}{order.mocked ? " · mocked (no PayPal credentials configured yet)" : ""}</p>
                <p className="mt-3 text-slate-600">In live mode, PayPal&rsquo;s SDK renders a payment button here that redirects to PayPal Sandbox, then returns to complete capture on the server. Until PayPal credentials are configured, use the button below to simulate a Sandbox capture against our backend.</p>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button onClick={captureOrder} disabled={busy} data-testid="pvc-enroll-mock-capture" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60">
                  Simulate PayPal Sandbox Capture <ArrowRight className="w-4 h-4" />
                </button>
                <Link to="/academy/patients-voice-in-care" className="text-xs text-slate-500 hover:underline">Cancel and return</Link>
              </div>
              <p className="mt-3 text-[11px] text-slate-500">Refreshing this page or navigating away and back will not create duplicate purchases or duplicate access — the server enforces idempotency by PayPal order id.</p>
            </div>
          )}

          {step === "success" && (
            <div className="text-center" data-testid="pvc-enroll-step-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" strokeWidth={1.5} />
              <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-emerald-700 font-semibold">Enrollment Complete</p>
              <h2 className="mt-2 font-heading text-3xl text-slate-900">You&rsquo;re enrolled in The Patient&rsquo;s Voice in Care.</h2>
              <p className="mt-3 text-sm text-slate-600">
                {result?.paypal_capture_id && <>Capture reference: <code className="text-xs">{result.paypal_capture_id}</code> · </>}
                Access granted after server verification. This is a Sandbox test.
              </p>
              <button onClick={()=>nav("/academy/portal/pvc-001/learn")} data-testid="pvc-enroll-begin" className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium">Begin Course <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}

          {step === "error" && (
            <div className="text-center" data-testid="pvc-enroll-step-error">
              <p className="text-[11px] uppercase tracking-[0.24em] text-amber-700 font-semibold">Payment {result?.status || "issue"}</p>
              <h2 className="mt-2 font-heading text-2xl text-slate-900">Access has not been granted.</h2>
              <p className="mt-3 text-sm text-slate-600 max-w-lg mx-auto leading-relaxed">
                {result?.status === "pending" && "PayPal reports the payment is still processing. Course access will unlock automatically once the capture completes."}
                {result?.status === "canceled" && "Checkout was canceled. No charge was made. You can try again anytime."}
                {result?.status === "failed" && "The payment was not completed. You may retry with the same account."}
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button onClick={()=>setStep("review")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium">Try again</button>
                <Link to="/academy/patients-voice-in-care" className="text-xs text-slate-500 hover:underline">Return to course info</Link>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 text-xs text-slate-500 leading-relaxed" data-testid="pvc-enroll-safeguards">
          <p><ShieldCheck className="inline w-3.5 h-3.5 text-[#4A6FA5] mr-1" /><strong>Safeguards active:</strong> access is granted only after a server-side capture verification with PayPal. Direct navigation to a success URL, page refreshes, or duplicate PayPal webhook deliveries cannot create duplicate purchases or duplicate access.</p>
        </div>
      </div>
    </div>
  );
}

function indexActive(step, i) {
  const order = ["email", "register", "signin", "review", "pay", "success", "error"];
  const stepIndex = { email: 0, register: 1, signin: 1, review: 2, pay: 3, success: 4, error: 4 };
  return (stepIndex[step] ?? 0) >= i;
}
